import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { ApolloClientOptions, ApolloLink, from, InMemoryCache, split } from '@apollo/client/core';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { ConnectionPagination } from '@models/apollo';
import {
  Certification,
  CertificationChangeRequest,
  Course,
  EnrolmentKey,
  Grant,
  IlBooking,
  IlLocationInterface,
  Invite,
  Learner,
  License,
  LicenseGroup,
  Organization,
  default as possibleTypesResult,
  PromoCode,
  SupportLicense,
  SurveySchema
} from '@queries/generated';
import { GlobalID } from '@util/global-id';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { isEqual } from 'lodash';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';

export function connectionPagination<T>() {
  return (existing: ConnectionPagination<T>, incoming: ConnectionPagination<T>): ConnectionPagination<T> => {
    if (existing) {
      if (isEqual(existing.pageInfo, incoming.pageInfo)) {
        return existing;
      }

      return {
        ...existing,
        pageInfo: incoming.pageInfo,
        nodes: [...existing.nodes, ...incoming.nodes]
      };
    }
    return incoming;
  };
}

function maybeParseDate(value: string) {
  if (value) {
    return new Date(value);
  }
  return null;
}

const dateField = {
  read: maybeParseDate
};

const dateFields = {
  createdAt: dateField,
  updatedAt: dateField
};

const commonLicenseFields = {
  enrolmentKeys: {
    keyArgs: ['withDeleted'],
    merge: connectionPagination<EnrolmentKey>()
  },
  invites: {
    keyArgs: [],
    merge: connectionPagination<Invite>()
  }
};

const fullName = {
  fullName: {
    read(value: string, { readField }) {
      if (value) {
        return value;
      }

      return `${readField('firstName')} ${readField('lastName')}`;
    }
  }
};

const ownerId = {
  ownerId: {
    read(_, { readField }) {
      return readField('gid', readField('owner'));
    }
  }
};

const sellCourse = {
  sellCourse: {
    read(_, { readField }) {
      return readField('productDefault') !== null;
    }
  }
};

const unauthorized = new Subject<boolean>();
unauthorized.pipe(first()).subscribe(() => {
  window.location.href = '/users/sign_in';
});

const errorLink = onError(({ networkError }) => {
  if (networkError) {
    console.log('[GraphQL] network error, redirecting to login');
    console.log(networkError);
    unauthorized.next(true);
  }
});

const auth = new ApolloLink((operation, forward) => {
  operation.setContext({ http: { includeQuery: true } });
  return forward(operation).map((response) => {
    const {
      response: { headers }
    } = operation.getContext();
    if (headers) {
      const gid = headers.get('X-RapidLMS-User');

      if (!gid) {
        console.log('[GraphQL] not logged in, redirecting');
        unauthorized.next(true);
        throw new Error('not logged in, redirecting');
      }
    }
    return response;
  });
});

const uri = '/graphql';

export function createApollo(httpLink: HttpLink, batchHttpLink: BatchHttpLink): ApolloClientOptions<any> {
  const cache = new InMemoryCache({
    possibleTypes: possibleTypesResult.possibleTypes,
    typePolicies: {
      SurveySchemasNamespace: {
        keyFields: [],
        fields: {
          all: {
            keyArgs: ['organizationIds'],
            merge: connectionPagination<SurveySchema>()
          }
        }
      },
      AutomationConstantsNamespace: {
        keyFields: []
      },
      AutomationScriptsNamespace: {
        keyFields: []
      },
      CertificationChangeRequestsNamespace: {
        keyFields: [],
        fields: {
          all: {
            merge: connectionPagination<CertificationChangeRequest>()
          }
        }
      },
      CertificationTemplatesNamespace: {
        keyFields: []
      },
      CertificationsNamespace: {
        keyFields: [],
        fields: {
          all: {
            keyArgs: ['search', 'onlyMine'],
            merge: connectionPagination<Certification>()
          }
        }
      },
      CommentsNamespace: {
        keyFields: []
      },
      CourseItemProgressionsNamespace: {
        keyFields: []
      },
      CoursesNamespace: {
        keyFields: [],
        fields: {
          all: {
            keyArgs: ['openlyLicensed'],
            merge: connectionPagination<Course>()
          },
          configuredForRegulatorSubmission: {
            keyArgs: ['organizationIds'],
            merge: connectionPagination<Course>()
          },
          availableForRegulatorSubmission: {
            keyArgs: ['organizationId'],
            merge: connectionPagination<Course>()
          },
          availableForTrainingMatrices: {
            keyArgs: ['organizationId'],
            merge: connectionPagination<Course>()
          }
        }
      },
      DiscussionMessagesNamespace: {
        keyFields: []
      },
      GradedAssignmentSubmissionsNamespace: {
        keyFields: []
      },
      GradersNamespace: {
        keyFields: []
      },
      GrantsNamespace: {
        keyFields: []
      },
      ILBookingsNamespace: {
        keyFields: []
      },
      ILClassesNamespace: {
        keyFields: []
      },
      ILCoursesNamespace: {
        keyFields: []
      },
      ILLocationsNamespace: {
        keyFields: [],
        fields: {
          all: {
            keyArgs: ['organizationIds'],
            merge: connectionPagination<IlLocationInterface>()
          }
        }
      },
      InstructorsNamespace: {
        keyFields: []
      },
      IntegrationsNamespace: {
        keyFields: []
      },
      LearnersNamespace: {
        keyFields: [],
        fields: {
          all: {
            keyArgs: ['organizationIds', 'active'],
            merge: connectionPagination<Learner>()
          }
        }
      },
      NitoBanksNamespace: {
        keyFields: []
      },
      NitoExamsNamespace: {
        keyFields: []
      },
      OfflineOrdersNamespace: {
        keyFields: []
      },
      OrdersNamespace: {
        keyFields: []
      },
      OrganizationsNamespace: {
        keyFields: [],
        fields: {
          all: {
            keyArgs: [],
            merge: connectionPagination<Organization>()
          },
          allForInstructor: {
            keyArgs: [],
            merge: connectionPagination<Organization>()
          }
        }
      },
      ProductsNamespace: {
        keyFields: []
      },
      PromoCodesNamespace: {
        keyFields: [],
        fields: {
          all: {
            keyArgs: ['storefrontId'],
            merge: connectionPagination<PromoCode>()
          }
        }
      },
      LicenseGroupsNamespace: {
        keyFields: [],
        fields: {
          all: {
            keyArgs: [],
            merge: connectionPagination<LicenseGroup>()
          }
        }
      },
      LearnerVerifiedNamespace: {
        keyFields: [],
        fields: {
          reviewableGrants: {
            keyArgs: ['filter'],
            merge: connectionPagination<Grant>()
          }
        }
      },
      RapidqlNamespace: {
        keyFields: []
      },
      ReportsNamespace: {
        keyFields: []
      },
      SavedQueriesNamespace: {
        keyFields: []
      },
      StorefrontTemplatesNamespace: {
        keyFields: []
      },
      StorefrontsNamespace: {
        keyFields: []
      },
      StripePlansNamespace: {
        keyFields: []
      },
      StripeProductsNamespace: {
        keyFields: []
      },
      StripeSubscriptionsNamespace: {
        keyFields: []
      },
      TaxesNamespace: {
        keyFields: []
      },
      UsersNamespace: {
        keyFields: []
      },
      Grant: {
        fields: {
          ...dateFields,
          firstName: {
            read(_, { readField }) {
              return readField('firstName', readField('user'));
            }
          },
          lastName: {
            read(_, { readField }) {
              return readField('lastName', readField('user'));
            }
          },
          email: {
            read(_, { readField }) {
              return readField('email', readField('user'));
            }
          },
          mobileNumber: {
            read(_, { readField }) {
              return readField('mobileNumber', readField('user'));
            }
          }
        }
      },
      Invite: {
        fields: {
          ...dateFields,
          firstName: {
            read(_, { readField }) {
              return readField('firstName', readField('learner'));
            }
          },
          lastName: {
            read(_, { readField }) {
              return readField('lastName', readField('learner'));
            }
          },
          email: {
            read(_, { readField }) {
              return readField('email', readField('learner'));
            }
          },
          mobileNumber: {
            read(_, { readField }) {
              return readField('mobileNumber', readField('learner'));
            }
          }
        }
      },
      Comment: {
        fields: dateFields
      },
      Certification: {
        fields: {
          ...dateFields,
          expiresAt: dateField,
          archivedAt: dateField
        }
      },
      ILCourse: {
        fields: dateFields
      },
      ILSession: {
        fields: {
          ...dateFields,
          startsAt: dateField,
          endsAt: dateField
        }
      },
      ILClass: {
        fields: {
          ...dateFields,
          // Don't use dateField on bookingStartsAt/bookingEndsAt because it breaks the whole sessionDate workflow.
          // This is also why ILSession doesn't mess with the dates
          bookingWindow: {
            read(_, { readField }) {
              const startsAt = maybeParseDate(readField('bookingStartsAt'));
              const firstSession = readField('ilSessions')[0];
              const endsAt = maybeParseDate(readField('bookingEndsAt') || readField('startsAt', firstSession));
              return { startsAt, endsAt };
            }
          },
          ilBookings: {
            keyArgs: [],
            merge: connectionPagination<IlBooking>()
          },
          waitlist: {
            keyArgs: [],
            merge: connectionPagination<IlBooking>()
          },
          startsAt: {
            read(_, { readField }) {
              const session = readField('ilSessions')[0];
              if (session) {
                return readField('startsAt', session);
              }
              return null;
            }
          }
        }
      },
      Course: {
        fields: {
          ...dateFields,
          expireLicensesAt: dateField,
          ...ownerId,
          ...sellCourse
        }
      },
      CourseProgression: {
        fields: {
          ...dateFields,
          startedAt: dateField,
          completedAt: dateField,
          failedAt: dateField
        }
      },
      CourseItemProgression: {
        fields: {
          ...dateFields,
          startedAt: dateField,
          completedAt: dateField,
          failedAt: dateField
        }
      },
      User: {
        fields: {
          ...fullName,
          name: {
            read(value: string, { readField }) {
              if (value) {
                return value;
              }

              return `${readField('fullName')}`;
            }
          }
        }
      },
      Learner: {
        fields: {
          ...fullName
        }
      },
      LicensePermissions: {
        // because LicenseGroup doesn't have a reasonable id
        keyFields: ['gid']
      },
      LicenseGroup: {
        // The id field is a string unlike everything else in the system
        // and we load these using the node entrypoint along with License
        // and we can't load both id because the types differ
        keyFields: ['gid'],
        fields: {
          ...dateFields,
          ...commonLicenseFields,
          id: {
            read(id, { readField }) {
              if (id) {
                return id;
              }
              const gid = GlobalID.decode(readField('gid') as string);
              return gid.id;
            }
          },
          licenses: {
            keyArgs: [],
            merge: connectionPagination<License>()
          }
        }
      },
      SupportedUser: {
        fields: {
          licenses: {
            keyArgs: [],
            merge: connectionPagination<SupportLicense>()
          }
        }
      },
      Owner: {
        keyFields: ['gid']
      },
      License: {
        fields: {
          ...dateFields,
          ...commonLicenseFields,
          isOpen: {
            read(_, { readField }) {
              return readField('seats') == null;
            }
          }
        }
      },
      EnrolmentKey: {
        fields: {
          ...dateFields,
          expiresAt: dateField,
          deletedAt: dateField,
          isDeleted: {
            read(_, { readField }) {
              return readField('deletedAt') != null;
            }
          }
        }
      },
      SavedQueryFilter: {
        keyFields: ['key', 'savedQueryId']
      },
      ShoppingCart: {
        keyFields: ['userId']
      },
      LedgerOrderItemRecord: {
        keyFields: ['gid', 'promoCodeId']
      },
      TermsOfService: {
        fields: {
          ...dateFields,
          agreed: {
            read(_, { readField }) {
              const ua = readField('userAgreements') as [];
              if (ua) {
                return ua.length > 0;
              }
              return false;
            }
          }
        }
      }
    }
  });
  const http = httpLink.create({ uri });
  const batchHttp = new BatchHttpLink({ uri });

  const splitLink = split(
    (operation) => {
      const { batch } = operation.getContext();
      return !!batch;
    },
    batchHttp,
    http
  );

  return {
    link: from([errorLink, auth, splitLink]),
    cache,
    defaultOptions: {
      query: {
        // Force query to network, watchQuery still uses cache.
        fetchPolicy: 'network-only'
      }
    }
  };
}

@NgModule({
  imports: [HttpClientModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink]
    }
  ]
})
export class GraphQLModule {}
